import { useCallback, useEffect } from 'react';
import { getIn } from 'formik';

import { FInput } from '../../../components/baseV2';
import { formatUsPhoneNumber, toUsPhoneDigits } from '../../../helpers';
import FormFieldReadOnlyValue from './FormFieldReadOnlyValue';
import { useFormReadOnly } from './formReadOnlyProvider';
import { DISABLE_BROWSER_AUTOFILL } from '../constants';

import type { ChangeEvent, FocusEvent } from 'react';
import type { FormikValues } from 'formik';
import type { FormPhoneFieldProps } from '../types';

export default function FormPhoneField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder = `Enter ${label.toLowerCase()}`,
  required = false,
  disabled = false,
  readOnly = false,
  showRequiredIndicator,
  sx,
  onBlur,
  onChange,
}: FormPhoneFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const isReadOnly = readOnly || readOnlyFromContext;
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = isTouched ? fieldError?.toString() : undefined;
  // Suppress the redundant "required field" message for empty required fields; the field still
  // highlights via `error` and its required indicator. Other validation messages still show.
  const helperText = required && !getIn(formik.values, name) ? undefined : errorMessage;

  const rawValue = getIn(formik.values, name) ?? '';
  const normalizedValue = toUsPhoneDigits(rawValue);
  const displayValue = formatUsPhoneNumber(rawValue);

  // Values from the backend or insurance-card extraction can arrive formatted (e.g. "(123) 456-7890").
  // Normalize the stored value to plain digits on load so the schema (`^\d{10}$` / length-10) validates
  // and the input never shows a valid-looking number that silently fails submission.
  useEffect(() => {
    if (rawValue !== normalizedValue) {
      void formik.setFieldValue(name, normalizedValue);
    }
  }, [name, rawValue, normalizedValue, formik.setFieldValue]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      formik.handleBlur(event);
      onBlur?.(name);
    },
    [formik, name, onBlur],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // Store the raw digits so the backend, schema, and read-only rendering all see a plain
      // 10-character string while the input displays the formatted value below.
      const digits = toUsPhoneDigits(event.target.value);
      void formik.setFieldValue(name, digits);
      onChange?.(name, digits);
    },
    [formik, name, onChange],
  );

  if (isReadOnly) {
    return <FormFieldReadOnlyValue label={label} required={required} value={displayValue} />;
  }

  return (
    <FInput
      autoComplete={DISABLE_BROWSER_AUTOFILL}
      disabled={disabled}
      error={Boolean(errorMessage)}
      fullWidth
      helperText={helperText}
      inputProps={{ inputMode: 'tel' }}
      label={label}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      sx={sx}
      value={displayValue}
    />
  );
}
